import { signal, Type } from '@angular/core';
import { property } from '@arcgis/core/core/accessorSupport/decorators';
import Collection from '@arcgis/core/core/Collection';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { getPseudoLayers } from './pseudo-layer/navigation-pseudo-layers';
import { NavigationPseudoLayer } from './pseudo-layer/navigation.pseudo-layer';
import { Persona, DEFAULT_PERSONA } from '../../../../types/persona.model';
import { IFR } from '../typings/ifr';
import {
  createLayerWithJepTools,
  ThemeAwareVectorTileLayer,
  JepVectorTileLayerProperties
} from '@jeppesen-foreflight/jmkw-types';

interface NavigationLayerProps {
  ifr: IFR;
  persona: Persona;
  _logger?: NGXLogger;
}

export class NavigationLayer extends createLayerWithJepTools<
  Type<ThemeAwareVectorTileLayer>,
  {
    toggleVisibility(visible?: boolean): boolean;
    toggleIFR(ifr?: IFR): IFR;
    togglePersona(persona?: Persona): Persona;
  },
  NavigationLayerProps
>(ThemeAwareVectorTileLayer, {
  toggleVisibility(visible?: boolean): boolean {
    this.visible = visible ?? !this.visible;
    return this.visible;
  },
  toggleIFR(ifr?: IFR): IFR {
    const newIfr: IFR = ifr ?? (this.ifr === 'Low' ? 'High' : 'Low');
    this._logger?.trace(
      `[NavigationLayer]: toggleIFR(${this.ifr} => ${newIfr})`
    );
    this.ifr = newIfr;
    return this.ifr;
  },
  togglePersona(persona?: Persona): Persona {
    const newPersona: Persona =
      persona ?? (this.persona === 'InFlight' ? 'Operations' : 'InFlight');
    this._logger?.trace(
      `[NavigationLayer]: togglePersona(${this.persona} => ${newPersona})`
    );
    this.persona = newPersona;
    return this.persona;
  }
}) {
  isJmkwLayer = true;
  defaultLayerProperties = { zOrder: 99 } as const;

  subLayers = signal<Collection<NavigationPseudoLayer>>(getPseudoLayers(this));

  private readonly _logger?: NGXLogger;
  private readonly _ifrSubject = new BehaviorSubject<IFR>('Low');
  readonly ifr$: Observable<IFR> = this._ifrSubject.asObservable();

  private readonly _personaSubject = new BehaviorSubject<Persona>(
    DEFAULT_PERSONA
  );
  readonly persona$: Observable<Persona> = this._personaSubject.asObservable();

  private _ifrSub?: Subscription;

  get ifr(): IFR {
    return this._ifrSubject.value;
  }
  set ifr(value: IFR) {
    this._ifrSubject.next(value);
  }

  get persona(): Persona {
    return this._personaSubject.value;
  }
  set persona(value: Persona) {
    this._personaSubject.next(value);
  }

  constructor(
    layerProperties?: JepVectorTileLayerProperties,
    persona?: Persona,
    logger?: NGXLogger
  ) {
    super(layerProperties);
    Object.assign(this, { ...this.defaultLayerProperties, ...layerProperties });
    this._logger = logger;
    this._ifrSub = this.ifr$.subscribe((ifr) =>
      this._toggleIfrOnSubLayers(ifr)
    );
    if (persona) this._personaSubject.next(persona);
  }

  @property({ aliasOf: 'layers' }) get layers() {
    return this.subLayers();
  }

  initializeIFRVectorStyles(): void {
    if (!this.loaded) return;
    this._logger?.debug(
      `[NavigationLayer] Set initial vector style visibility (${this.ifr})`
    );
    this._toggleIfrOnSubLayers(this.ifr);
  }

  private _toggleIfrOnSubLayers(
    ifr: IFR,
    subLayers: Collection<NavigationPseudoLayer> = this.subLayers()
  ): void {
    subLayers.forEach((pseudoLayer: NavigationPseudoLayer) => {
      pseudoLayer.jepTools.toggleIFR(ifr);
      if (pseudoLayer.layers?.length) {
        this._toggleIfrOnSubLayers(
          ifr,
          pseudoLayer.layers as Collection<NavigationPseudoLayer>
        );
      }
    });
  }

  override destroy(): void {
    this._ifrSub?.unsubscribe();
    super.destroy();
  }
}
